<template lang="pug">

  transition(
    @enter="enter" 
    @leave="leave" 
    :css="false" 
    appear
  )
 
    #home

      #sections( ref="sections" )

        welcome-section
        five-star-section
        four-star-section
        three-star-section
        two-star-section
        one-star-section
        cta-section

      pagination(v-if="showPagination")

      .overlay(v-if="!isHomeRoute" @click="onOverlayClick")

      router-view



</template>

<script>
 
import gsap from 'gsap'
import { mapState, mapMutations, mapGetters } from 'vuex'  
import Env from 'src/config/env'
import { PATHS, HOME, MODAL_STATES } from 'src/config/app'
import Scroll from '@/helpers/Scroll'
import Pointer from '@/helpers/Pointer'
import { map } from '@/helpers/MathUtils'

import WelcomeSection from '@/components/home/sections/Welcome'
import FiveStarSection from '@/components/home/sections/FiveStar'
import FourStarSection from '@/components/home/sections/FourStar'
import ThreeStarSection from '@/components/home/sections/ThreeStar'
import TwoStarSection from '@/components/home/sections/TwoStar'
import OneStarSection from '@/components/home/sections/OneStar'
import CtaSection from '@/components/home/sections/Cta'
import Pagination from '@/components/home/Pagination'

export default {

  name: PATHS.HOME,

  components: {
    WelcomeSection,
    FiveStarSection,
    FourStarSection,
    ThreeStarSection,
    TwoStarSection,
    OneStarSection,
    CtaSection,
    Pagination
  },

  data() {
    return {
      scroll_ratio: 0,
      scroll_y: 0,
      y_delta: 0,
      threshold: 5,

      events: {
        added: false
      }
    }
  },

  computed: {
 
    ...mapState('app', [ 
      'modal_state',
      'motion_clamp',
      'home' 
    ]),  

    isHomeRoute() {
      return ( this.$route.name === PATHS.HOME )
    },

    showPagination(){
      return ( Env.desktop || Env.tablet )
    }
  },

  mounted() {
 
    if(!Env.touch_device){ 
      this.scroll = new Scroll()
    }
    
    this.updateEvents()
  },

  beforeDestroy() {
    this.removeEvents()
  },

  methods: {

    ...mapMutations('app',[
      'prevHomeSection',
      'nextHomeSection',
      'setHomeSectionByIndex'
    ]), 

    updateModal(){ 
      switch(this.modal_state) {
        case MODAL_STATES.NONE:
          this.modalNone()
          break;
        case MODAL_STATES.MENU:
          this.modalMenu()
          break; 
      }
    },

    modalNone(){
      gsap.killTweensOf(this.$refs.sections)
      gsap.to(this.$refs.sections, {
        duration: (1.5 * this.motion_clamp),
        ease: 'power3.out',  
        x: 0
      })
      gsap.to(this.$refs.sections, {
        duration: (1 * this.motion_clamp),
        ease: 'power2.in', 
        delay: 0.5 * this.motion_clamp,
        autoAlpha: 1 
      })
    },

    modalMenu(){
      gsap.killTweensOf(this.$refs.sections) 
      gsap.set(this.$refs.sections, { autoAlpha: 0 }) 
    },

    updateRoute(){ 
      switch(this.$route.name) {
        case PATHS.HOME:
          this.routeHome()
          break;
        case PATHS.RATING_ONE: 
        case PATHS.RATING_TWO: 
        case PATHS.RATING_THREE: 
        case PATHS.RATING_FOUR:
        case PATHS.RATING_FIVE:
          this.routeContent()
          break; 
      } 
    },

    routeHome(){ 
      gsap.killTweensOf(this.$refs.sections)
      gsap.to(this.$refs.sections, {
        duration: (1.5 * this.motion_clamp),
        ease: 'power3.out',  
        x: 0
      })
      gsap.to(this.$refs.sections, {
        duration: (1 * this.motion_clamp),
        delay: (0.5 * this.motion_clamp),
        ease: 'power2.in', 
        autoAlpha: 1 
      }) 
    },

    routeContent(){ 
      gsap.killTweensOf(this.$refs.sections) 
      gsap.set(this.$refs.sections, { autoAlpha: 0 })
    }, 

    addEvents(){
      if(!this.events.added){
        if(Env.touch_device){ 
          Pointer.on(Pointer.Events.MOVE, this.onPointerMove)
        } else {
          this.scroll.on(Scroll.Events.Wheel, this.onWheel)
        }
        this.events.added = true
      }
    },

    removeEvents(){
      if(Env.touch_device){
        Pointer.off(Pointer.Events.MOVE, this.onPointerMove);
      } else {
        this.scroll.off(Scroll.Events.Wheel, this.onWheel);
      }
      this.events.added = false
    },

    onWheel(){
      if(this.modal_state === MODAL_STATES.NONE){
        this.y_delta = (this.scroll.y.delta * 0.1);
        if(this.y_delta < -5){
          this.back() 
        } else if(this.y_delta > 5){
          this.next() 
        }
      } 
    },

    onPointerMove(){

      if( this.modal_state === MODAL_STATES.NONE ){
        if( Pointer.axis === Pointer.Constants.AXIS.Y ){
          let triggered = Math.abs(Pointer.delta.y) > this.threshold
          if( triggered ){
            switch(Pointer.direction) {
              case Pointer.Constants.DIRECTION.DOWN:
                this.back()
                break;
              case Pointer.Constants.DIRECTION.UP:
                this.next()
                break;
            }
          }
        }
      }
    },

    onOverlayClick(){
      this.$router.replace(PATHS.HOME)
    },

    back(){
      this.prevHomeSection()
    },

    next(){
      this.nextHomeSection()
    },

    updateEvents(){
      let isModalStateNone = ( this.modal_state === MODAL_STATES.NONE )
      if( this.isHomeRoute && isModalStateNone){
        this.addEvents()
      } else {
        this.removeEvents()
      }
    },

    onEnterComplete(){  
      // clear preload styling
      const elem = document.getElementById('pre-loading-section')
      elem.parentNode.removeChild(elem)
      document.body.style.backgroundImage = 'url("")' 
    },

    enter(el, done){

      gsap.killTweensOf(el)

      done()  
      gsap.to(el, { 
        duration: 2 * this.motion_clamp,
        opacity: 1, 
        ease: 'power3.inOut' 
      }) 
      gsap.to(document.getElementById('pre-loading-section'), { 
        duration: 0.5 * this.motion_clamp,
        delay: 1 * this.motion_clamp,
        opacity: 0, 
        ease: 'linear',
        onComplete: this.onEnterComplete
      })  
    },

    leave(el, done){ 
      gsap.killTweensOf(el)  
      gsap.to(this.$el, { 
        duration: ( (this.base_duration * 0.36 ) * this.motion_clamp ),
        opacity: 0, 
        ease: 'power3.in'
      }) 
      done()
    }

  },

  watch: {
    '$route'(val){
      this.updateEvents()
      this.updateRoute()
    },

    'modal_state'(val){
      this.updateEvents()
      this.updateModal()
    }
  }, 

}
</script>

<style lang="sass" scoped>
@import "src/styles/common.sass"

#home
  +full()
  +flex-center()
  opacity: 0

  .overlay
    +full()
    cursor: pointer
    opacity: 0


  #sections
    overflow: hidden
    position: relative
    width: 100%
    height: 100%


</style>
